import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Spacer from "../Spacer";
import { IFUIcon } from "../icons/IFUIcon";
import { getPdxIfuHref } from "../../utils/navigation/ifuLink";
import { QUESTIONNAIRES } from "../../utils/constants";

const QUESTIONNAIRE_BY_SLUG = new Map(
  QUESTIONNAIRES.map((questionnaire) => [questionnaire.slug, questionnaire]),
);

export function MenuPanel({
  onClose,
  showQuestionnaires = true,
  questionnaireSlugs = [],
  dense = false,
}) {
  const { t, i18n } = useTranslation(["common", "legal"]);
  const [questionnairesOpen, setQuestionnairesOpen] = useState(false);
  const lang = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en-GB"
  ).toLowerCase();
  const ifuHref = getPdxIfuHref(lang);

  // Only the questionnaires this user is actually assigned, in catalogue order
  // rather than whatever order the assignment arrived in.
  const assignedQuestionnaires = QUESTIONNAIRES.filter((questionnaire) =>
    questionnaireSlugs.includes(questionnaire.slug),
  ).concat(
    // Anything assigned that the catalogue does not know about would otherwise
    // vanish from the menu with no trace, so keep it with its slug as a label.
    questionnaireSlugs
      .filter((slug) => !QUESTIONNAIRE_BY_SLUG.has(slug))
      .map((slug) => ({ slug, labelKey: null })),
  );

  const rowBase = dense
    ? "w-full flex flex-row justify-between items-center subtitle-text min-h-[40px]"
    : "w-full flex flex-row justify-between items-center subtitle-text min-h-[44px]";
  const row = `${rowBase} hover:underline`;
  // The row that expands carries no underline: a decoration inherited by the
  // chevron rotates with it into a stray vertical line, and on touch the hover
  // state sticks after the tap that opened the list.
  const expandableRow = rowBase;
  // Indented under the row that reveals them, and lighter than a top-level
  // destination so the nesting reads without a second divider. Only the label
  // underlines, for the same reason the chevron above is left alone.
  const subRow = [
    "w-full flex flex-row justify-between items-center gap-2 pl-4 pr-1",
    "text-left",
    dense ? "min-h-[36px]" : "min-h-[40px]",
  ].join(" ");

  return (
    <div className="flex flex-col gap-1 items-center w-full max-w-lg md:pt-4">
      <div className="w-full flex flex-row justify-end items-center min-h-[44px]">
        <button
          type="button"
          className="small-outlined-button !min-h-0 !inline-flex !flex-row !items-center !justify-center !gap-1 whitespace-nowrap group !min-w-fit"
          onClick={onClose}
          aria-label={t("navBar.closeMenu", "Close menu")}
        >
          <span
            className="material-symbols-outlined text-[24px] text-primary hover:!text-greyscale-1 group-hover:text-greyscale-1"
            aria-hidden="true"
          >
            close
          </span>
          <span className="leading-none">{t("close", "Close")}</span>
        </button>
      </div>

      <Spacer />

      <Link to="/profile" onClick={onClose} className={row}>
        {t("navBar.profile", "Profile")}
        <span
          className="material-symbols-outlined text-[24px]"
          aria-hidden="true"
        >
          arrow_forward_ios
        </span>
      </Link>

      {showQuestionnaires ? (
        <>
          <Spacer />

          {assignedQuestionnaires.length ? (
            <>
              <button
                type="button"
                className={expandableRow}
                aria-expanded={questionnairesOpen}
                aria-controls="menu-questionnaires"
                onClick={() => setQuestionnairesOpen((open) => !open)}
              >
                {t("navBar.questionnaires", "Questionnaires")}
                <span
                  className={[
                    "material-symbols-outlined text-[24px] transition-transform duration-200",
                    questionnairesOpen ? "rotate-90" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-hidden="true"
                >
                  arrow_forward_ios
                </span>
              </button>

              {questionnairesOpen ? (
                <div
                  id="menu-questionnaires"
                  className="w-full flex flex-col gap-1"
                >
                  {assignedQuestionnaires.map(({ slug, labelKey }) => (
                    <Link
                      key={slug}
                      to={`/questionnaires/${slug}`}
                      onClick={onClose}
                      className={subRow}
                    >
                      <span className="truncate hover:underline">
                        {labelKey ? t(labelKey, slug) : slug}
                      </span>
                      <span
                        className="material-symbols-outlined text-[20px] shrink-0"
                        aria-hidden="true"
                      >
                        arrow_forward_ios
                      </span>
                    </Link>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            // Nothing to expand into — keep the row as the direct destination
            // rather than a control that opens an empty list.
            <Link to="/questionnaires" onClick={onClose} className={row}>
              {t("navBar.questionnaires", "Questionnaires")}
              <span
                className="material-symbols-outlined text-[24px]"
                aria-hidden="true"
              >
                arrow_forward_ios
              </span>
            </Link>
          )}
        </>
      ) : null}

      <Spacer />

      <a
        href={ifuHref}
        target="_blank"
        rel="noopener noreferrer"
        className={row}
        aria-label={t("navBar.ifu", "Instructions For Use")}
        onClick={onClose}
      >
        <span className="flex items-center gap-2">
          <span>{t("navBar.ifu", "Instructions For Use")}</span>
          <IFUIcon
            className="h-6 w-auto shrink-0 fill-typography"
            aria-hidden="true"
          />
        </span>
        <span
          className="material-symbols-outlined text-[24px]"
          aria-hidden="true"
        >
          open_in_new
        </span>
      </a>

      <Spacer />

      <Link to="/guidance" onClick={onClose} className={row}>
        {t("navBar.guidance", "Tips")}
        <span
          className="material-symbols-outlined text-[24px]"
          aria-hidden="true"
        >
          arrow_forward_ios
        </span>
      </Link>

      <Spacer />

      <Link to="/about" onClick={onClose} className={row}>
        {t("navBar.about", "About")}
        <span
          className="material-symbols-outlined text-[24px]"
          aria-hidden="true"
        >
          arrow_forward_ios
        </span>
      </Link>
    </div>
  );
}
